import DOMPurify from 'dompurify';
const DANGEROUS_PROTOCOLS = [
  'javascript:',
  'data:',
  'vbscript:',
  'file:',
  'about:',
  'chrome:',
  'chrome-extension:',
  'moz-extension:'
];
export const sanitizeInput = (input) => {
    if (input === null || input === undefined) {
        return '';
    }

    // Handle non-string inputs
    if (typeof input !== 'string') {
        input = String(input);
    }

    // Remove dangerous protocols instead of entire input
    DANGEROUS_PROTOCOLS.forEach(protocol => {
        input = input.replace(new RegExp(protocol, 'gi'), '');
    });

    const config = {
        ALLOWED_TAGS: ['p', 'b', 'i', 'em', 'strong', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
        ALLOW_DATA_ATTR: false,
        ADD_ATTR: ['rel'],
        FORCE_BODY: true,
        SANITIZE_DOM: true,
        WHOLE_DOCUMENT: false,
        RETURN_DOM_FRAGMENT: false,
        RETURN_DOM: false,
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|xxx):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
    };

    return DOMPurify.sanitize(input, config);
};

// Auto-prepend https:// when a user types a bare domain (e.g. "csun.protopia.co")
// instead of a full URL — required downstream since `new URL()` needs a scheme.
export const ensureHttpsScheme = (url) => {
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

// Add a specific URL sanitizer
export const sanitizeUrl = (url) => {
    if (!url) return '';

    let sanitized = url;
    // Remove dangerous protocols instead of entire URL
    DANGEROUS_PROTOCOLS.forEach(protocol => {
        sanitized = sanitized.replace(new RegExp(protocol, 'gi'), '');
    });

    sanitized = sanitizeInput(sanitized);
    return sanitized.replace(/[^\w-\.\:\/@\?\&\=\%\#]/gi, '');
};

export const sanitizeHtmlTemplate = (template) => {
  if (!template) return '';

  // Extracting all {{…}} placeholders
  const placeholders = [];
  const tokenized = template.replace(/\{\{[\s\S]*?\}\}/g, match => {
    const idx = placeholders.push(match) - 1;
    return `___TPL_${idx}___`;
  });

  // Checking if this is a full HTML document
  const isFullDocument = /^\s*<!DOCTYPE|<html/i.test(tokenized);

  // Existing DOMPurify config
  const config = {
    ALLOWED_TAGS: [
      'html', 'head', 'body', 'title', 'meta', 'style', 'link',
      'p', 'div', 'span', 'br', 'hr',
      'b', 'i', 'em', 'strong', 'u', 'sub', 'sup', 'font',
      'ul', 'ol', 'li',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
      'a', 'img',
      'center', 'spacer', '!doctype', 'xml', 'o:officedocumentsettings', 'o:allowpng', 'o:pixelsperinch',
      'v:*', 'o:*', 'w:*',
      '!--[if', '![endif]--', '!--', '--'
    ],
    ALLOWED_ATTR: [
      'class', 'id', 'style', 'align', 'valign', 'dir', 'lang',
      'cellpadding', 'cellspacing', 'border', 'bgcolor', 'width', 'height',
      'href', 'target', 'rel', 'name',
      'src', 'alt', 'title', 'border', 'hspace', 'vspace', 'style',
      'xmlns', 'xmlns:v', 'xmlns:o', 'xmlns:w', 'background',
      'http-equiv', 'content', 'charset',
      'media', 'type',
      'urn:schemas-microsoft-com:office:office',
      'role', 'aria-*', 'data-*',
      'colspan', 'rowspan', 'scope',
      'face', 'size', 'color'
    ],
    ALLOW_DATA_ATTR: true,
    ADD_ATTR: ['target'],
    FORBID_ATTR: [
      'onabort', 'onblur', 'onchange', 'onclick', 'ondblclick', 'onerror', 'onfocus',
      'onkeydown', 'onkeypress', 'onkeyup', 'onload', 'onmousedown', 'onmousemove',
      'onmouseout', 'onmouseover', 'onmouseup', 'onreset', 'onresize', 'onscroll',
      'onselect', 'onsubmit', 'onunload'
    ],
    FORBID_TAGS: [
      'script', 'object', 'embed', 'param', 'iframe', 'frame', 'frameset',
      'form', 'input', 'button', 'select', 'option'
    ],
    SAFE_FOR_TEMPLATES: true,
    WHOLE_DOCUMENT: isFullDocument,
    SANITIZE_DOM: true,
    KEEP_CONTENT: true,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM: false,
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|maps|data|cid):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
  };

  // First pass: strip dangerous protocols
  let safe = tokenized;
  DANGEROUS_PROTOCOLS.forEach(proto => {
    safe = safe.replace(new RegExp(proto, 'gi'), '');
  });

  // Second pass: DOMPurify
  safe = DOMPurify.sanitize(safe, config);

  // Restoring {{…}} placeholders
  return safe.replace(/___TPL_(\d+)___/g, (_, idx) => placeholders[+idx]);
};

/* Decode so "Q&A" does not show as "Q&amp;A" */
export const decodeHtmlEntitiesForPlainText = (str) => {
  if (str == null || str === '') {
    return str;
  }
  if (typeof str !== 'string') {
    return str;
  }
  if (typeof document === 'undefined') {
    return str
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#0*39;/g, "'")
      .replace(/&#x0*27;/gi, "'");
  }
  const t = document.createElement('textarea');
  t.innerHTML = str;
  return t.value;
};

/* Hermes Action Form tips: same security as sanitizeHtmlTemplate, store/display plain & */
export const sanitizeActionFormTips = (value) => {
  return decodeHtmlEntitiesForPlainText(sanitizeHtmlTemplate(value));
};

export const sanitizeResourceDescription = (description) => {
    const config = {
        ALLOWED_TAGS: ['a', 'strong', 'em', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'style'],
        ALLOW_DATA_ATTR: false,
        FORCE_BODY: true
    };

    return DOMPurify.sanitize(description, config);
};

// Sanitize CSS to prevent XSS attacks
export function sanitizeCss(css) {
    if (!css) return '';

    // First decode any encoded content to handle it properly
    let sanitized = css
        // Decode URL encoded content
        .replace(/%[0-9A-F]{2}/gi, '')
        // Decode HTML entities
        .replace(/&[#\w]+;/gi, '')
        // Remove base64 content
        .replace(/base64,\s*[A-Za-z0-9+/=]+/g, '')
        // Remove alert statements
        .replace(/alert\s*\([^)]*\)/gi, '')
        // Remove script tags and content
        .replace(/<\/?script[^>]*>/gi, '')
        // Remove HTML tags
        .replace(/<[^>]*>/g, '')
        // Remove quotes and angle brackets
        .replace(/['"<>]/g, '')
        // Remove event handlers
        .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
        // Remove dangerous attributes
        .replace(/\s*(onerror|onmouseover|onload|onclick)\s*=\s*["'][^"']*["']/gi, '');

    // Then apply CSS-specific sanitization
    sanitized = sanitized
        // Remove comments
        .replace(/\/\*[\s\S]*?\*\//g, '')
        // Remove imports
        .replace(/@import\s+[^;]*;?/g, '')
        // Remove all url() functions
        .replace(/url\s*\([^)]*\)/gi, '')
        // Remove expression and other dangerous functions
        .replace(/(expression|eval|exec|function|call|apply)\s*\([^)]*\)/gi, '')
        .replace(/calc\s*\([^)]*\)/gi, '')
        // Remove behavior properties
        .replace(/behavior\s*:[^;]*/gi, '')
        // Remove bindings
        .replace(/-moz-binding\s*:[^;]*/gi, '')
        // Remove position properties
        .replace(/(position\s*:\s*fixed|position\s*:\s*absolute)\s*;?/gi, '')
        // Remove z-index
        .replace(/z-index\s*:[^;]*/gi, '')
        // Remove javascript
        .replace(/javascript:/gi, '')
        // Remove data URIs
        .replace(/data:\s*[^;]*;?/gi, '')
        // Remove dangerous properties
        .replace(/-o-link\s*:[^;]*/gi, '')
        .replace(/-o-link-source\s*:[^;]*/gi, '')
        // Remove background with protocols
        .replace(/background(?:-image)?\s*:\s*[^;]*(?:url|http|https|file|ftp):[^;]*;?/gi, '')
        // Remove CSS variables
        .replace(/var\s*\([^)]*\)/gi, '')
        // Remove potentially dangerous at-rules
        .replace(/@(charset|import|namespace|supports|document)\s+[^;{]*[;{]?/gi, '')
        // Remove filters
        .replace(/filter\s*:[^;]*/gi, '')
        // Remove webkit specific dangerous properties
        .replace(/-webkit-link\s*:[^;]*/gi, '')
        // Remove content property which can be used for injection
        .replace(/content\s*:[^;]*/gi, '')
        // Final cleanup of any remaining script-like content
        .replace(/script/gi, '')
        .replace(/expression/gi, '')
        .replace(/javascript/gi, '')
        .replace(/alert/gi, '')
        .replace(/eval/gi, '')
        // Remove any remaining parentheses pairs that might contain code
        .replace(/\([^)]*\)/g, '');

    return sanitized;
}

// Sanitize scripts to prevent XSS attacks
export function sanitizeScript(script) {
    if (!script) return '';

    // List of allowed script sources (update based on your requirements)
    const allowedScriptSources = [
        'https://storage.googleapis.com/protopia/',
        'https://cdn.jsdelivr.net/',
        'https://cdnjs.cloudflare.com/'
    ];

    // Check if script source is from allowed domains
    const isAllowedSource = allowedScriptSources.some(source => script.startsWith(source));

    if (!isAllowedSource) {
        console.warn('Blocked potentially unsafe script source:', script);
        return '';
    }

    return script;
}

// Sanitize HTML attributes to prevent XSS attacks
export function sanitizeHtmlAttributes(attributes) {
    if (!attributes || typeof attributes !== 'object') return {};

    const sanitizedAttrs = {};
    for (const [key, value] of Object.entries(attributes)) {
        // Remove potentially dangerous attributes
        if (key.toLowerCase().startsWith('on') || // Remove event handlers
            key.toLowerCase() === 'href' && value.toLowerCase().includes('javascript:') || // Remove javascript: URLs
            key.toLowerCase() === 'src' && !sanitizeScript(value)) { // Validate script sources
            continue;
        }

        sanitizedAttrs[key] = typeof value === 'string' ? sanitizeInput(value) : value;
    }

    return sanitizedAttrs;
}

// Add after existing sanitizer functions
export const testSanitization = {
  runTests() {
    const tests = [
      {
        name: 'Resource Description Test',
        input: '<a href="https://example.com">Link</a> with <script>alert(1)</script>',
        expected: '<a href="https://example.com">Link</a> with ',
        type: 'resource'
      },
      {
        name: 'Template Test',
        input: '<tr><td onclick="alert(1)"><a href="http://example.com">Link</a></td></tr>',
        expected: '<tr><td><a href="http://example.com">Link</a></td></tr>',
        type: 'template'
      },
      {
        name: 'URL Test',
        input: 'javascript:alert(1)//http://example.com',
        expected: 'http://example.com',
        type: 'url'
      }
    ];

    tests.forEach(test => {
      let result;
      switch(test.type) {
        case 'resource':
          result = sanitizeResourceDescription(test.input);
          break;
        case 'template':
          result = sanitizeHtmlTemplate(test.input);
          break;
        case 'url':
          result = sanitizeUrl(test.input);
          break;
        default:
          result = sanitizeInput(test.input);
      }

      console.log(`${test.name}:`);
      console.log('Input:', test.input);
      console.log('Result:', result);
      console.log('Expected:', test.expected);
      console.log('Passed:', result === test.expected);
      console.log('-------------------');
    });
  }
};

export const runSanitizerTests = () => {
  const tests = {
    basicTests: [
      {
        name: 'Remove Script Tags',
        input: '<script>alert("xss")</script>Hello',
        expected: 'Hello',
        fn: sanitizeInput
      },
      {
        name: 'Allow Permitted HTML',
        input: '<p><strong>Hello</strong> <em>World</em></p>',
        expected: '<p><strong>Hello</strong> <em>World</em></p>',
        fn: sanitizeInput
      }
    ],
    urlTests: [
      {
        name: 'Sanitize Malicious URL',
        input: 'javascript:alert("xss")',
        expected: '',
        fn: sanitizeUrl,
        assert: (result) => !result.includes('javascript:')
      },
      {
        name: 'Allow Valid URL',
        input: 'https://example.com/path?param=value',
        expected: 'https://example.com/path?param=value',
        fn: sanitizeUrl
      }
    ],
    templateTests: [
      {
        name: 'Allow Table Elements',
        input: '<table><tr><td style="color: red;">Hello</td></tr></table>',
        fn: sanitizeHtmlTemplate,
        assert: (result) => {
          return result.includes('<table>') &&
                 result.includes('<td') &&
                 result.includes('style="color: red;"');
        }
      }
    ]
  };

  // Run tests and log results
  Object.entries(tests).forEach(([category, testCases]) => {
    console.group(`%c${category}`, 'color: blue; font-weight: bold');

    testCases.forEach(test => {
      const result = test.fn(test.input);
      const passed = test.assert ? test.assert(result) : result === test.expected;

      console.log(
        `%c${test.name}:`,
        `color: ${passed ? 'green' : 'red'}; font-weight: bold`
      );
      console.log('Input:', test.input);
      console.log('Result:', result);
      if (!test.assert) console.log('Expected:', test.expected);
      console.log('Passed:', passed);
      console.log('-------------------');
    });

    console.groupEnd();
  });
};


// WEBPACK FOOTER //
// ./src/utils/sanitizer.js